import { i18n } from '@/plugins/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';
import { handleAxiosError } from '@/utils/error.utils';
import { getBuConfigForContext, getLoginRouteNameForPath } from '@/utils/bu.utils';
import { getAccountStrategy } from '@/utils/middleware.utils';
import { toast } from '@/utils/toast.utils';
import type { NavigationGuard, RouteLocationNormalized } from 'vue-router';

const clearAuthSession = (authStore: ReturnType<typeof useAuthStore>) => {
  if (typeof localStorage?.clear === 'function') {
    localStorage.clear();
  }
  authStore.access_token = undefined;
};

// Shared auth flow for Flokee/Cleepo routes to avoid duplicated guard logic.
const ensureAuthenticated = async (
  to: RouteLocationNormalized,
  next: Parameters<NavigationGuard>[2],
  loginRouteName: string,
) => {
  const { t } = i18n.global as any;
  const authStore = useAuthStore();
  const uiStore = useUiStore();

  uiStore.setLoading(true);
  if (!authStore.access_token) {
    uiStore.setLoading(false);
    authStore.lastRoutePath = to.fullPath;
    return next({ name: loginRouteName });
  }

  if (!authStore.user) {
    try {
      await authStore.invalidate();
      await authStore.getCurrentUser();
    } catch (error) {
      clearAuthSession(authStore);
      toast.error({ message: t('notifications.unauthenticated_error') });
      uiStore.setLoading(false);
      authStore.lastRoutePath = to.fullPath;
      return next({ name: loginRouteName });
    }
  }

  uiStore.setLoading(false);
  if (authStore.lastRoutePath) {
    next(authStore.lastRoutePath);
    authStore.lastRoutePath = undefined;
  } else {
    next();
  }
};

export const authMiddlewares: Record<string, NavigationGuard> = {
  isFlokeeAccount: async (_to, _, next) => {
    const authStore = useAuthStore();

    if (authStore.isCleepoAccount) return next({ name: 'CleepoDashboardView' });

    next();
  },
  isCleepoAccount: async (_to, _, next) => {
    const authStore = useAuthStore();

    if (authStore.isFlokeeAccount) return next({ name: 'DashboardView' });

    next();
  },
  isAppLogin: async (to, _, next) => {
    // If user hits a login page for the wrong BU, redirect to the correct BU login.
    const config = getBuConfigForContext({
      path_value: to.path,
      origin_value: window.location.origin,
      use_stored: true,
    });

    if (config && !to.path.startsWith(config.basePath)) {
      return next({ name: config.loginRouteName });
    }

    next();
  },
  shouldRedirectToDashboard: async (_to, _, next) => {
    const authStore = useAuthStore();
    const uiStore = useUiStore();

    uiStore.setLoading(true);

    if (authStore.access_token) {
      uiStore.setLoading(false);
      if (authStore.isFlokeeAccount) return next({ name: 'DashboardView' });
      if (authStore.isCleepoAccount) return next({ name: 'CleepoDashboardView' });
    }

    uiStore.setLoading(false);

    next();
  },
  isAuthenticated: async (_to, _, next) => {
    const loginRouteName = getLoginRouteNameForPath({
      path_value: _to.path,
      origin_value: window.location.origin,
      use_stored: true,
    });

    return ensureAuthenticated(_to, next, loginRouteName);
  },
  isAuthenticatedCleepo: async (_to, _, next) => {
    return ensureAuthenticated(_to, next, 'CleepoAuthLoginView');
  },
  isAccountConfigured: async (_to, _, next) => {
    const authStore = useAuthStore();

    if (!authStore.user) {
      authStore.logout();
      return next({ name: 'AuthLoginView' });
    }

    const strategy = getAccountStrategy(_to);

    // Execute validation steps
    const accountRedirect = await strategy.validateAccount();
    if (accountRedirect) {
      return next({ name: accountRedirect });
    }

    const isInvalidOrder = !authStore.user.current_order_id;

    if (isInvalidOrder) {
      const orderRedirect = await strategy.handleNoCurrentOrder();
      if (orderRedirect) {
        return next({ name: orderRedirect });
      }
    }

    if (strategy.validateIntegration) {
      const integrationRedirect = await strategy.validateIntegration();
      if (integrationRedirect) {
        return next({ name: integrationRedirect });
      }
    }

    return next();
  },
  verifyAuthentication: async (_to, _, next) => {
    const authStore = useAuthStore();
    const uiStore = useUiStore();
    uiStore.setLoading(true);

    if (!authStore.user && authStore.access_token) {
      try {
        await authStore.getCurrentUser();
      } catch (error) {
        const { message } = handleAxiosError(error);
        toast.error({ message });
      }
    }

    uiStore.setLoading(false);

    next();
  },
  isAdmin: async (_to, _, next) => {
    const authStore = useAuthStore();

    if (!authStore.user && authStore.access_token) {
      return next({ name: 'AuthLoginView' });
    }

    if (authStore.user?.role.id !== 1 && authStore.user?.role.id !== 3) {
      return next({ name: 'AuthLoginView' });
    }

    next();
  },
};
